import { useLantern, useSession } from "../hooks";
import { navigate, useRoute } from "../router";
import { AccountMenu } from "./AccountMenu";
import { createContext, useContext, useEffect, useRef, useState, type DragEvent, type FormEvent, type ReactNode } from "react";
import { isClosed, looksUnread, waitingCount, type ChannelState, type State } from "../sync/store";
import type { Session } from "../session";
import { addSection, arrange, builtin, CHANNELS, DMS, moveChannel, moveSection, removeSection, sectionName, sectionOf, STARRED, updateSection, type Placed } from "../sidebar";
import { modKey } from "./Shortcuts";
import { PopMenu, type MenuItem } from "./PopMenu";
import { IconBell, IconBookmark, IconChevron, IconClock, IconClose, IconCollapse, IconCompose, IconDown, IconEdit, IconFile, IconFolder, IconGear, IconHeadphones, IconInbox, IconPeople, IconReply, IconSearch, IconSend, IconStar, IconTask, IconTrash, IconUnread, IconUp } from "./icons";
import { WorkspaceMenu } from "./WorkspaceMenu";
import { t, tc, tn } from "../i18n";
import { HuddleMark } from "./Huddle";
import { Form } from "./ui";
import { clearHistory } from "./ChannelDetails";

// sidebarSections is the sidebar's channels and DMs in their sections:
// starred, the person's own, then the rest by name or newest message.
export function sidebarSections(session: Session, state: State): Placed<ChannelState>[] {
  const all = [...state.channels.values()].filter((c) => c.channel.kind !== "agent");
  const isDM = (c: ChannelState) => c.channel.kind === "dm" || c.channel.kind === "group_dm";
  const recent = state.sidebarSort === "recent" ? (c: ChannelState) => c.channel.last_message_at : undefined;
  return arrange(state.sidebar, all, (c) => c.channel.id, isDM, (c) => session.channelName(c.channel.id), recent);
}

// sidebarOrder is the channels the sidebar shows, top to bottom; the
// keyboard moves through them in this order.
export function sidebarOrder(session: Session, state: State, current?: string): string[] {
  return sidebarSections(session, state).flatMap((p) => shownIn(p, state, current).map((c) => c.channel.id));
}

// A collapsed section, or any with unread only set, still shows the
// channel one is in and those with something unread. Closed DMs, and
// archived channels unless one asks for them, show only while open.
function shownIn({ section, items }: Placed<ChannelState>, state: State, current?: string) {
  const open = items.filter((c) => c.channel.id === current || (!isClosed(c.channel) && (state.sidebarShowArchived || !c.channel.archived_at)));
  return section.collapsed || state.sidebarShow === "unread" ? open.filter((c) => c.channel.id === current || looksUnread(c.channel) || waitingCount(c.channel) > 0) : open;
}

type Drop = { section: string; before?: string };

export const connectionLabel = (c: State["connection"]) => ({ connecting: t("connecting"), online: t("online"), offline: t("offline"), signed_out: t("signed out") })[c];
const presenceLabel = (p?: string) => (p === "active" ? t("active") : p === "away" ? t("away") : t("offline"));

// The pages above the channels fold away, for a short window: folded, only
// the one open and those with news show, as in a collapsed section. It is
// kept in this browser, since it is about the screen.
const pagesKey = "lantern.sidebarPages";
const PagesFolded = createContext(false);

function usePagesFolded() {
  const [folded, setFolded] = useState(() => {
    try {
      return localStorage.getItem(pagesKey) === "folded";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      if (folded) localStorage.setItem(pagesKey, "folded");
      else localStorage.removeItem(pagesKey);
    } catch {
      // Private windows may refuse; it lasts until reload.
    }
  }, [folded]);
  return [folded, setFolded] as const;
}

// NavLink is one of the sidebar's pages above the channels: Inbox, Threads
// and the rest, marked while it is open. With news, it shows when folded.
function NavLink({ path, icon, label, className = "", news = false, children }: { path: string; icon: ReactNode; label: string; className?: string; news?: boolean; children?: ReactNode }) {
  useRoute();
  const folded = useContext(PagesFolded);
  const here = location.pathname.startsWith(path);
  if (folded && !here && !news) return null;
  return (
    <button className={`search-link ${here ? "active" : ""} ${className}`} aria-current={here ? "page" : undefined} onClick={() => navigate(path)}>
      {icon}
      <span className="nav-label">{label}</span>
      {children}
    </button>
  );
}

export function Sidebar({ current, onBrowse, onJump, onSignOut }: { current?: string; onBrowse: () => void; onJump: () => void; onSignOut: () => void }) {
  const session = useSession();
  const state = useLantern();
  const placed = sidebarSections(session, state);
  const [menu, setMenu] = useState<{ anchor: DOMRect; label: string; items: MenuItem[] } | null>(null);
  const [pagesFolded, setPagesFolded] = usePagesFolded();
  const [workspaceMenu, setWorkspaceMenu] = useState<DOMRect | null>(null);
  // A section being named: a new one (with a channel to put in it), or one
  // being renamed.
  const [naming, setNaming] = useState<{ id?: string; channel?: string } | null>(null);
  const [drag, setDrag] = useState<string | null>(null);
  const [drop, setDropState] = useState<Drop | null>(null);
  // The drop can come before a render with the last place dragged over.
  const dropRef = useRef<Drop | null>(null);
  const setDrop = (d: Drop | null) => {
    dropRef.current = d;
    setDropState(d);
  };
  const arrangeSidebar = session.arrangeSidebar.bind(session);
  const custom = state.sidebar.filter((x) => !builtin(x.id));
  const due = state.reminders?.filter((r) => r.fired_at).length ?? 0;
  const unreadChannels = [...state.channels.values()].filter((c) => !c.channel.archived_at && looksUnread(c.channel)).length;
  const waiting = state.scheduled?.length ?? 0;
  const failed = state.scheduled?.filter((m) => m.failed_at).length ?? 0;

  function rowMenu(id: string): MenuItem[] {
    const where = sectionOf(state.sidebar, id);
    const items: MenuItem[] = [
      where === STARRED
        ? { label: t("Unstar"), icon: <IconStar size={16} filled />, run: () => arrangeSidebar((x) => moveChannel(x, id, CHANNELS)) }
        : { label: t("Star"), icon: <IconStar size={16} />, run: () => arrangeSidebar((x) => moveChannel(x, id, STARRED)) },
    ];
    for (const sec of custom) if (sec.id !== where) items.push({ label: t("Move to {section}", { section: sec.name ?? "" }), icon: <IconFolder size={16} />, run: () => arrangeSidebar((x) => moveChannel(x, id, sec.id)) });
    items.push({ label: t("Move to a new section…"), icon: <IconFolder size={16} mark="plus" />, run: () => setNaming({ channel: id }) });
    const own = custom.find((x) => x.id === where);
    if (own) items.push({ label: t("Remove from {section}", { section: own.name ?? "" }), icon: <IconFolder size={16} mark="minus" />, run: () => arrangeSidebar((x) => moveChannel(x, id, CHANNELS)) });
    const muted = state.channels.get(id)?.channel.membership?.notify_level === "none";
    items.push({
      label: muted ? t("Unmute") : t("Mute"),
      icon: <IconBell size={16} off={!muted} />,
      divider: true,
      run: () => {
        const level = muted ? "mentions" : "none";
        void session.api.setNotifyLevel(id, level).then(() =>
          session.store.update((s) => {
            const m = s.channels.get(id)?.channel.membership;
            if (m) m.notify_level = level;
          }),
        );
      },
    });
    const kind = state.channels.get(id)?.channel.kind;
    if (kind === "dm" || kind === "group_dm")
      items.push(
        {
          label: t("Close conversation"),
          icon: <IconClose size={16} />,
          run: () => {
            if (id === current) navigate("/");
            void session.setClosed(id, true).catch(() => {});
          },
        },
        { label: t("Clear history…"), icon: <IconTrash size={16} />, danger: true, run: () => void clearHistory(session, id).catch((err) => window.alert(String(err))) },
      );
    return items;
  }
  function sectionMenu(id: string): MenuItem[] {
    if (builtin(id)) return [{ label: t("New section…"), icon: <IconFolder size={16} mark="plus" />, run: () => setNaming({}) }];
    const i = state.sidebar.findIndex((x) => x.id === id);
    const items: MenuItem[] = [{ label: t("Rename…"), icon: <IconEdit size={16} />, run: () => setNaming({ id }) }];
    if (i > 0) items.push({ label: t("Move up"), icon: <IconUp size={16} />, run: () => arrangeSidebar((x) => moveSection(x, id, -1)) });
    if (i < state.sidebar.length - 1) items.push({ label: t("Move down"), icon: <IconDown size={16} />, run: () => arrangeSidebar((x) => moveSection(x, id, 1)) });
    items.push({ label: t("Delete section"), icon: <IconTrash size={16} />, danger: true, run: () => arrangeSidebar((x) => removeSection(x, id)) });
    return items;
  }

  // optionsMenu is the gear's: what the sidebar lists and in what order,
  // and its sections.
  function optionsMenu(): MenuItem[] {
    const open = state.sidebar.some((x) => !x.collapsed);
    return [
      { label: t("Show"), heading: true },
      { label: t("All conversations"), checked: state.sidebarShow === "all", run: () => session.sidebarOptions({ show: "all" }) },
      { label: t("Unreads only"), checked: state.sidebarShow === "unread", run: () => session.sidebarOptions({ show: "unread" }) },
      { label: t("Archived channels"), checked: state.sidebarShowArchived, toggle: true, run: () => session.sidebarOptions({ show_archived: !state.sidebarShowArchived }) },
      { label: t("Sort channels and DMs"), heading: true, divider: true },
      { label: t("Alphabetically"), checked: state.sidebarSort === "name", run: () => session.sidebarOptions({ sort: "name" }) },
      { label: t("Most recent"), checked: state.sidebarSort === "recent", run: () => session.sidebarOptions({ sort: "recent" }) },
      { label: t("New section…"), icon: <IconFolder size={16} mark="plus" />, divider: true, run: () => setNaming({}) },
      {
        label: open ? t("Collapse all sections") : t("Expand all sections"),
        icon: <IconCollapse size={16} open={!open} />,
        run: () => arrangeSidebar((x) => x.map((sec) => ({ ...sec, collapsed: open }))),
      },
    ];
  }

  function create(name: string, channel?: string) {
    arrangeSidebar((x) => {
      const [next, id] = addSection(x, name);
      return channel ? moveChannel(next, channel, id) : next;
    });
  }

  // Dragging a channel: over a row in a section one orders, it goes above
  // or below that row; over Channels or Direct messages, back there.
  function over(e: DragEvent, section: string, id?: string, next?: string) {
    if (!drag) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    let to: Drop = { section };
    if (id && section !== CHANNELS && section !== DMS) {
      const r = e.currentTarget.getBoundingClientRect();
      to = { section, before: e.clientY < r.top + r.height / 2 ? id : next };
    }
    const at = dropRef.current;
    if (to.section !== at?.section || to.before !== at?.before) setDrop(to);
  }
  function dropped(e: DragEvent) {
    e.preventDefault();
    const to = dropRef.current;
    if (drag && to && to.before !== drag) arrangeSidebar((x) => moveChannel(x, drag, to.section, to.before));
    setDrag(null);
    setDrop(null);
  }

  const item = (c: ChannelState, section: string, next?: ChannelState) => {
    // Every unread message in a DM is for you; in a channel, mentions. What
    // turns a channel bold follows the member's notify level.
    const unread = looksUnread(c.channel);
    const badge = waitingCount(c.channel);
    const muted = c.channel.membership?.notify_level === "none";
    // A DM shows whether the other person is online.
    const others = (session.dmMembers.get(c.channel.id) ?? []).filter((id) => id !== state.me?.id);
    const online = c.channel.kind === "dm" && others.length === 1 ? state.presence.get(others[0]) : undefined;
    return (
      <li
        key={c.channel.id}
        className={`${drop?.section === section && drop.before === c.channel.id ? "drop-before" : ""} ${drag === c.channel.id ? "dragging" : ""}`}
        onDragOver={(e) => over(e, section, c.channel.id, next?.channel.id)}
      >
        <button
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData("text/plain", session.channelName(c.channel.id));
            e.dataTransfer.effectAllowed = "move";
            setDrag(c.channel.id);
          }}
          onDragEnd={() => (setDrag(null), setDrop(null))}
          className={`channel ${c.channel.id === current ? "active" : ""} ${unread ? "unread" : ""} ${c.channel.archived_at ? "archived" : ""} ${muted ? "muted" : ""}`}
          onClick={() => navigate(`/c/${c.channel.id}`)}
          aria-current={c.channel.id === current ? "page" : undefined}
          title={session.channelName(c.channel.id)}
        >
          <span className="name">
            {c.channel.kind === "dm" && <span className={`presence ${online ?? "offline"}`} aria-label={presenceLabel(online)} />}
            {c.channel.kind === "private" && "🔒 "}
            {session.channelName(c.channel.id)}
            {c.channel.kind === "dm" && others.length === 1 && session.statusOf(others[0])?.emoji && (
              <span className="status-emoji" title={session.statusOf(others[0])!.text}>
                {" "}
                {session.statusOf(others[0])!.emoji}
              </span>
            )}
          </span>
          <HuddleMark channel={c.channel.id} />
          {muted && badge === 0 && (
            <span className="muted-mark" title={t("Muted")} aria-label={t("Muted")}>
              🔕
            </span>
          )}
          {badge > 0 ? (
            <span className="badge">{badge > 99 ? "99+" : badge}</span>
          ) : (
            state.drafts.has(c.channel.id) &&
            c.channel.id !== current && (
              <span className="draft-mark" title={t("You have an unsent draft here")} aria-label={t("Draft")}>
                ✏️
              </span>
            )
          )}
        </button>
        <button
          className="row-menu"
          aria-label={t("Channel options")}
          title={t("Channel options")}
          aria-haspopup="menu"
          onClick={(e) => setMenu({ anchor: e.currentTarget.getBoundingClientRect(), label: t("Channel options"), items: rowMenu(c.channel.id) })}
        >
          ⋯
        </button>
      </li>
    );
  };

  return (
    <nav className="sidebar" aria-label={t("Channels")}>
      <div className="me">
        <button
          className="workspace-button"
          aria-haspopup="menu"
          aria-expanded={!!workspaceMenu}
          onClick={(e) => setWorkspaceMenu(workspaceMenu ? null : e.currentTarget.getBoundingClientRect())}
        >
          <strong className="workspace-name">{state.workspace || "Lantern"}</strong>
          <IconChevron size={14} />
        </button>
        <span className={`dot ${state.connection}`} title={connectionLabel(state.connection)} />
        <span className="head-tools">
          <button
            className="head-tool"
            aria-label={t("Sidebar options")}
            title={t("Sidebar options")}
            aria-haspopup="menu"
            onClick={(e) => setMenu({ anchor: e.currentTarget.getBoundingClientRect(), label: t("Sidebar options"), items: optionsMenu() })}
          >
            <IconGear size={17} />
          </button>
          <button className="head-tool" aria-label={t("New message")} title={t("New message")} onClick={() => navigate("/new")}>
            <IconCompose size={17} />
          </button>
        </span>
      </div>
      {workspaceMenu && <WorkspaceMenu anchor={workspaceMenu} onClose={() => setWorkspaceMenu(null)} />}
      <button className="jump-to phone-only" onClick={onJump}>
        <span>{t("Jump to…")}</span>
        <kbd>{modKey}K</kbd>
      </button>
      <section className="side-section side-pages" aria-label={tc("sidebar", "Pages")}>
        <h2>
          <button className="section-toggle" aria-expanded={!pagesFolded} onClick={() => setPagesFolded(!pagesFolded)}>
            <span className="caret" aria-hidden>
              {pagesFolded ? "▸" : "▾"}
            </span>
            {tc("sidebar", "Pages")}
          </button>
        </h2>
        <PagesFolded.Provider value={pagesFolded}>
          <NavLink path="/inbox" icon={<IconInbox size={17} />} label={t("Inbox")} news={due > 0}>
            {due > 0 && (
              <span className="badge thread-badge" title={tn(due, "A reminder is due", "{n} reminders are due")}>
                {due}
              </span>
            )}
          </NavLink>
          <NavLink path="/unreads" icon={<IconUnread size={17} />} label={t("Unreads")} className={unreadChannels > 0 ? "has-unread" : ""} news={unreadChannels > 0}>
            {unreadChannels > 0 && (
              <span className="count" title={tn(unreadChannels, "A channel has new messages", "{n} channels have new messages")}>
                {unreadChannels}
              </span>
            )}
          </NavLink>
          <NavLink path="/threads" icon={<IconReply size={17} />} label={t("Threads")} news={state.unreadThreads > 0}>
            {state.unreadThreads > 0 && <span className="badge thread-badge">{state.unreadThreads > 99 ? "99+" : state.unreadThreads}</span>}
          </NavLink>
          {state.huddlesOn && (
            <NavLink path="/huddles" icon={<IconHeadphones size={17} />} label={t("Huddles")} news={state.huddles.size > 0}>
              {state.huddles.size > 0 && (
                <span className="count" title={tn(state.huddles.size, "A huddle is going on", "{n} huddles are going on")}>
                  {state.huddles.size}
                </span>
              )}
            </NavLink>
          )}
          <NavLink path="/saved" icon={<IconBookmark size={17} />} label={t("Saved")} />
          <NavLink path="/files" icon={<IconFile size={17} />} label={t("Files")} />
          <NavLink path="/drafts" icon={<IconSend size={17} />} label={t("Drafts & sent")}>
            {state.drafts.size > 0 && <span className="count">{state.drafts.size}</span>}
          </NavLink>
          {waiting > 0 && (
            <NavLink path="/scheduled" icon={<IconClock size={17} />} label={t("Scheduled")} news={failed > 0}>
              <span className={failed > 0 ? "badge thread-badge" : "count"} title={failed > 0 ? t("A scheduled message could not be sent") : undefined}>
                {waiting}
              </span>
            </NavLink>
          )}
          <NavLink path="/people" icon={<IconPeople size={17} />} label={t("People")} />
          <NavLink path="/search" icon={<IconSearch size={17} />} label={t("Search")} className="phone-only" />
          <NavLink path="/tasks" icon={<IconTask size={17} />} label={t("Tasks")} />
        </PagesFolded.Provider>
      </section>
      {typeof Notification !== "undefined" && Notification.permission === "default" && (
        <button className="search-link" onClick={() => void Notification.requestPermission().then(() => session.store.update(() => {}))}>
          <IconBell size={17} />
          <span className="nav-label">{t("Enable notifications")}</span>
        </button>
      )}
      {placed.map((p) => {
        const { section } = p;
        // Starred shows while something in it does, archived channels hidden.
        if (section.id === STARRED && !p.items.some((c) => c.channel.id === current || state.sidebarShowArchived || !c.channel.archived_at)) return null;
        const name = sectionName(section);
        const shown = shownIn(p, state, current);
        return (
          <section
            key={section.id}
            className={`side-section ${drop?.section === section.id && !drop.before ? "drop" : ""}`}
            aria-label={name}
            onDragOver={(e) => over(e, section.id)}
            onDrop={dropped}
          >
            {section.id === CHANNELS && naming && !naming.id && <SectionName onDone={(n) => (n && create(n, naming.channel), setNaming(null))} />}
            <h2>
              {naming?.id === section.id ? (
                <SectionName initial={name} onDone={(n) => (n && arrangeSidebar((x) => updateSection(x, section.id, { name: n })), setNaming(null))} />
              ) : (
                <button className="section-toggle" aria-expanded={!section.collapsed} onClick={() => arrangeSidebar((x) => updateSection(x, section.id, { collapsed: !section.collapsed }))}>
                  <span className="caret" aria-hidden>
                    {section.collapsed ? "▸" : "▾"}
                  </span>
                  {name}
                </button>
              )}
              <span className="section-tools">
                <button
                  className="section-menu"
                  aria-label={t("Section options")}
                  title={t("Section options")}
                  aria-haspopup="menu"
                  onClick={(e) => setMenu({ anchor: e.currentTarget.getBoundingClientRect(), label: t("Section options"), items: sectionMenu(section.id) })}
                >
                  ⋯
                </button>
                {section.id === CHANNELS && (
                  <button className="add" onClick={onBrowse} aria-label={t("Browse or create channels")}>
                    +
                  </button>
                )}
              </span>
            </h2>
            <ul>{shown.map((c, i) => item(c, section.id, shown[i + 1]))}</ul>
          </section>
        );
      })}
      {menu && <PopMenu {...menu} onClose={() => setMenu(null)} />}
      <AccountMenu onSignOut={onSignOut} />
    </nav>
  );
}

// SectionName asks for a section's name in place.
function SectionName({ initial = "", onDone }: { initial?: string; onDone: (name: string | null) => void }) {
  const [name, setName] = useState(initial);
  function submit(e: FormEvent) {
    e.preventDefault();
    onDone(name.trim() || null);
  }
  return (
    <Form className="section-name" onSubmit={submit}>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => onDone(null)}
        onKeyDown={(e) => e.key === "Escape" && onDone(null)}
        placeholder={t("Section name")}
        aria-label={t("Section name")}
        maxLength={40}
        autoFocus
      />
    </Form>
  );
}
